/* CrmListShell — structural scaffolding for the 6 CRM list views
 *
 * Wraps the shared "chrome" rows (topbar, toolbar, quick-filters, content
 * container) for contacts / companies / leads / deals / tickets / calls.
 *
 * This component renders ONLY the container `<div>`s and CSS classes that
 * are shared across all 6 list views. All conditional logic, state, and
 * per-view rendering lives in the slot props passed in by the parent.
 *
 * R8b extraction: lifted from `App.tsx` lines ~34889-39407 so per-view
 * bodies can be peeled into their own lazy chunks (CallsList first).
 *
 * NO LOGIC moves here — just structural HTML.
 */
import type React from 'react'

export type CrmListView = 'contacts' | 'companies' | 'leads' | 'deals' | 'tickets' | 'calls'

export type CrmListShellProps = {
  /** The currently-active list view. Used by the parent for slot composition;
   *  the shell itself doesn't switch on it (kept for future per-view styling). */
  activeView: CrmListView

  // ---------- Topbar ----------
  /** Object switcher dropdown + separator + view tabs row (rendered inside
   *  `.hs-topbar-left`). The parent assembles this from the object switcher,
   *  separator, view-tabs, and the +add view button. */
  topbarLeft: React.ReactNode

  /** Right-side primary action (Add contacts / Add deals / Log call / etc.)
   *  rendered inside `.hs-topbar-right`. */
  topbarRight: React.ReactNode

  // ---------- Toolbar ----------
  /** Search input + viewtype + edit columns + filters + export — rendered
   *  inside `.hs-toolbar-left`. */
  toolbarLeft: React.ReactNode

  /** Clone + save view buttons — rendered inside `.hs-toolbar-right`. */
  toolbarRight: React.ReactNode

  // ---------- Quick filters ----------
  /** Quick filter pills + owner dropdown + cycle toggles + sort + +More.
   *  Pass `null` when `quickFiltersOpen === false` so the row collapses
   *  entirely (NOT just hidden via CSS — matches the original conditional). */
  quickFilters: React.ReactNode | null

  // ---------- Body ----------
  /** Per-view body. For deals this is the kanban or table view. For
   *  contacts/companies/leads/tickets/calls this is the table-area with
   *  selection bars, table, and pagination. */
  body: React.ReactNode

  /** Whether the right-side preview drawer is open — adds the `with-preview`
   *  modifier class to `.hs-content` (controls grid template). */
  hasPreview: boolean

  /** Whether the table-settings side panel is open — adds the `with-settings`
   *  modifier class to `.hs-content`. */
  tableSettingsOpen: boolean

  /** Loading overlay bar (rendered after `.hs-content` closes). Pass `null`
   *  when not loading. */
  loadingBar: React.ReactNode | null
}

export default function CrmListShell({
  topbarLeft,
  topbarRight,
  toolbarLeft,
  toolbarRight,
  quickFilters,
  body,
  hasPreview,
  tableSettingsOpen,
  loadingBar,
}: CrmListShellProps) {
  const contentClass = `hs-content${hasPreview ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`
  return (
    <div className="hs-contacts">
      {/* Top bar: object switcher + view tabs + actions */}
      <div className="hs-topbar">
        <div className="hs-topbar-left">{topbarLeft}</div>
        <div className="hs-topbar-right">{topbarRight}</div>
      </div>

      {/* Toolbar — HubSpot-style */}
      <div className="hs-toolbar">
        <div className="hs-toolbar-left">{toolbarLeft}</div>
        <div className="hs-toolbar-right">{toolbarRight}</div>
      </div>

      {/* Quick filters bar — toggled by Filters button */}
      {quickFilters !== null && <div className="hs-quick-filters">{quickFilters}</div>}

      {/* Main content area */}
      <div className={contentClass}>{body}</div>

      {/* Loading overlay */}
      {loadingBar}
    </div>
  )
}
